import { Link } from '@tanstack/react-router';
import { DateTime, T, Var, useGT } from 'gt-react';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import type { TaskHelpRequestEvidence as Evidence } from '@erp/rpc';
import {
  helpRequestEvidenceView,
  type HelpRequestFailedStepView,
} from './taskHelpRequestEvidenceView';

// A help request can wait days, so the time alone would read as today.
const FAILED_AT_OPTIONS = { dateStyle: 'medium', timeStyle: 'short' } as const;

/**
 * What the escalating attempt left behind, under the agent's question: the
 * step that failed and why, the operator's notes, the screen at the failure,
 * and a link to the run. Renders nothing when there is no evidence.
 */
export function TaskHelpRequestEvidence({
  evidence,
}: {
  evidence: Evidence | null | undefined;
}) {
  const view = helpRequestEvidenceView(evidence);
  if (!view) return null;
  return (
    <div
      data-testid="task-help-request-evidence"
      className="mt-2 flex flex-col gap-1.5 rounded border border-[oklch(0.9_0.05_80)] bg-white/70 p-2 text-xs text-stone-700"
    >
      {view.failedStep && <FailedStep step={view.failedStep} />}
      {view.runPath && (
        <Link
          {...view.runPath}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 self-start font-medium text-blue-700 hover:underline"
        >
          <T context="link opening the agent run (trace and screen recording) that escalated a task">
            Open the run
          </T>
          <ArrowSquareOutIcon className="size-3" />
        </Link>
      )}
    </div>
  );
}

function FailedStep({ step }: { step: HelpRequestFailedStepView }) {
  const gt = useGT();
  return (
    <>
      <div className="font-medium text-stone-900">
        <T context="where an AI agent's procedure failed: step number, step name, and the date and time it failed">
          Failed at step <Var>{step.stepIndex}</Var>: <Var>{step.stepName}</Var>{' '}
          (<DateTime options={FAILED_AT_OPTIONS}>{step.failedAt}</DateTime>)
        </T>
      </div>
      {step.procedureName && (
        <div className="text-stone-500">{step.procedureName}</div>
      )}
      {step.error && <div className="text-stone-600">{step.error}</div>}
      {step.latestNote && (
        <div className="line-clamp-6 whitespace-pre-wrap border-l-2 border-stone-300 pl-2 text-stone-700">
          {step.latestNote}
        </div>
      )}
      {step.earlierNotes.length > 0 && (
        <details>
          <summary className="cursor-pointer text-stone-500">
            <T context="toggle showing the AI operator's earlier notes from the failed step">
              Earlier notes (<Var>{step.earlierNotes.length}</Var>)
            </T>
          </summary>
          <ul className="mt-1 flex flex-col gap-1">
            {step.earlierNotes.map((note) => (
              <li key={note.id} className="whitespace-pre-wrap">
                {note.text}
              </li>
            ))}
          </ul>
        </details>
      )}
      {step.screenshotUrl && (
        <a
          href={step.screenshotUrl}
          target="_blank"
          rel="noreferrer"
          className="self-start"
        >
          <img
            src={step.screenshotUrl}
            alt={gt('Screen when the step failed', {
              context:
                'alt text of a screenshot taken by an AI agent at the moment its procedure step failed',
            })}
            className="max-h-48 rounded border border-stone-200"
          />
        </a>
      )}
    </>
  );
}
